import { useSyncExternalStore } from 'react'
import { z } from 'zod'

const storageKey = 'bv_customer_tracked_orders_v1'

const trackedOrderSchema = z.object({
  orderId: z.string().min(1).max(200),
  email: z.string().min(3).max(320),
  addedAt: z.string().optional(),
})

const trackedListSchema = z.array(trackedOrderSchema).max(50)

export type TrackedOrder = z.infer<typeof trackedOrderSchema>

let cachedRaw: string | null | undefined = undefined
let cachedList: TrackedOrder[] = []

function read(): TrackedOrder[] {
  if (typeof window === 'undefined') return []
  try {
    const raw = window.localStorage.getItem(storageKey)
    if (raw === cachedRaw) return cachedList
    cachedRaw = raw
    if (!raw) {
      cachedList = []
      return cachedList
    }
    const parsed = trackedListSchema.safeParse(JSON.parse(raw))
    if (!parsed.success) {
      cachedList = []
      return cachedList
    }
    cachedList = parsed.data
    return cachedList
  } catch {
    cachedRaw = undefined
    cachedList = []
    return cachedList
  }
}

function write(next: TrackedOrder[]) {
  if (typeof window === 'undefined') return
  const raw = JSON.stringify(next)
  cachedRaw = raw
  cachedList = next
  window.localStorage.setItem(storageKey, raw)
  window.dispatchEvent(new Event('bv_tracked_orders_changed'))
}

function normalizeEmail(email: string): string {
  return email.trim().toLowerCase()
}

function normalizeId(orderId: string): string {
  return orderId.trim()
}

function keyOf(orderId: string, email: string): string {
  return `${normalizeId(orderId)}::${normalizeEmail(email)}`
}

export const TrackedOrdersStore = {
  useTrackedOrders(): TrackedOrder[] {
    return useSyncExternalStore(
      (onStoreChange) => {
        const onCustom = () => onStoreChange()
        const onStorage = (e: StorageEvent) => {
          if (!e.key || e.key === storageKey) onStoreChange()
        }
        window.addEventListener('bv_tracked_orders_changed', onCustom)
        window.addEventListener('storage', onStorage)
        return () => {
          window.removeEventListener('bv_tracked_orders_changed', onCustom)
          window.removeEventListener('storage', onStorage)
        }
      },
      () => read(),
      () => [],
    )
  },

  getOne(orderId: string): TrackedOrder | null {
    const id = normalizeId(orderId)
    const all = read()
    return all.find((o) => normalizeId(o.orderId) === id) ?? null
  },

  add(input: { orderId: string; email: string }): boolean {
    const orderId = normalizeId(input.orderId)
    const email = normalizeEmail(input.email)
    if (!orderId || !email) return false
    const now = new Date().toISOString()
    const all = read()
    const k = keyOf(orderId, email)
    if (all.some((o) => keyOf(o.orderId, o.email) === k)) return false
    const next: TrackedOrder[] = [{ orderId, email, addedAt: now }, ...all].slice(0, 50)
    write(next)
    return true
  },

  remove(input: { orderId: string; email: string }) {
    const orderId = normalizeId(input.orderId)
    const email = normalizeEmail(input.email)
    const k = keyOf(orderId, email)
    const all = read()
    const next = all.filter((o) => keyOf(o.orderId, o.email) !== k)
    write(next)
  },

  clear() {
    write([])
  },
} as const

